import type { ReactNode } from 'react';

/** The neutral, single-select filter chips shared with recipe discovery. */
interface FilterChipRowProps {
  options: Array<{ value: string; label: string }>;
  selected: string | null;
  onSelect: (value: string | null) => void;
  ariaLabel?: string;
}

export function FilterChip({ label, active, onClick, className = '', children }: {
  label: string; active: boolean; onClick: () => void; className?: string; children?: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={active}
      onClick={onClick}
      className={`cursor-pointer rounded-lg px-3 py-1.5 text-sm transition-colors duration-150 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring motion-reduce:transition-none ${className} ${
        active
          ? 'bg-foreground text-background'
          : 'bg-muted text-muted-foreground hover:text-foreground'
      }`}
    >
      {children ?? label}
    </button>
  );
}

/** Keep All distinct from an option whose actual value is literally "all". */
export function FilterChipRow({ options, selected, onSelect, ariaLabel }: FilterChipRowProps) {
  return (
    <div role={ariaLabel ? 'group' : undefined} aria-label={ariaLabel} className="flex flex-wrap items-center gap-2">
      <FilterChip label="All" active={selected === null} onClick={() => onSelect(null)} />
      {options.map(({ value, label }) => <FilterChip key={value} label={label} active={selected === value} onClick={() => onSelect(value)} />)}
    </div>
  );
}
